import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import DatePicker from '../../components/DatePicker';
import { Label } from '../../components/ui/label';

const DueDateCalculator = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [lmp, setLmp] = useState(today);

  const milestones = useMemo(() => {
    const start = new Date(lmp);
    if (isNaN(start)) return null;
    const due = new Date(start); due.setDate(due.getDate() + 280);
    const conception = new Date(start); conception.setDate(conception.getDate() + 14);
    const t1End = new Date(start); t1End.setDate(t1End.getDate() + 7 * 13);
    const t2End = new Date(start); t2End.setDate(t2End.getDate() + 7 * 27);
    const viability = new Date(start); viability.setDate(viability.getDate() + 7 * 24);
    return { due, conception, t1End, t2End, viability };
  }, [lmp]);

  return (
    <CalculatorLayout
      title="Due Date Calculator"
      subtitle="Find your baby's expected due date and pregnancy milestones."
      category="Fitness"
      description={<p>Based on the first day of your last menstrual period (LMP), this calculator estimates your due date and key pregnancy milestones using Naegele's rule (LMP + 280 days).</p>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">First Day of Last Period (LMP)</Label>
            <DatePicker value={lmp} onChange={setLmp} />
            <p className="text-[11px] text-slate-400 mt-1">Type or pick from calendar</p>
          </div>
        </div>

        {milestones && (
          <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estimated Due Date</div>
            <div className="font-display text-2xl md:text-3xl font-bold text-bharat-navy display-font mt-1">
              {milestones.due.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div className="mt-5 space-y-2">
              <Milestone week="Wk 2" label="Conception (estimate)" date={milestones.conception} />
              <Milestone week="Wk 13" label="End of 1st Trimester" date={milestones.t1End} tone="saffron" />
              <Milestone week="Wk 24" label="Viability (medical)" date={milestones.viability} />
              <Milestone week="Wk 27" label="End of 2nd Trimester" date={milestones.t2End} tone="saffron" />
              <Milestone week="Wk 40" label="Due Date" date={milestones.due} tone="green" big />
            </div>
          </div>
        )}
      </div>
    </CalculatorLayout>
  );
};

const Milestone = ({ week, label, date, tone, big }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-slate-700';
  return (
    <div className={`flex items-center justify-between bg-white rounded-md p-3 border ${big ? 'border-indgreen-300' : 'border-slate-100'}`}>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{week}</div>
        <div className="text-sm text-slate-700">{label}</div>
      </div>
      <div className={`display-font font-semibold ${c} ${big ? 'text-base' : 'text-sm'}`}>
        {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </div>
    </div>
  );
};

export default DueDateCalculator;
